import React, { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Searchable single-select combobox.
export const SearchableSelect = ({ options = [], value, onChange, placeholder = 'Select…', testid, allowClear = true }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="flex w-full items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm text-left hover:bg-gray-50">
          <span className={cn('truncate', !selected && 'text-slate-400')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem value="__clear__" onSelect={() => { onChange(''); setOpen(false); }}>
                  <span className="text-slate-400">— None —</span>
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem key={o.value} value={`${o.label} ${o.sublabel || ''}`}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  onSelect={() => { onChange(o.value); setOpen(false); }}>
                  <Check size={14} className={cn('mr-2', value === o.value ? 'opacity-100 text-[#8FC93A]' : 'opacity-0')} />
                  <span className="flex flex-col">
                    <span>{o.label}</span>
                    {o.sublabel && <span className="text-xs text-slate-400">{o.sublabel}</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Searchable multi-select with chips.
export const SearchableMultiSelect = ({ options = [], value = [], onChange, placeholder = 'Select…', testid }) => {
  const [open, setOpen] = useState(false);
  const set = new Set(value || []);
  const toggle = (v) => {
    const next = new Set(set);
    next.has(v) ? next.delete(v) : next.add(v);
    onChange(Array.from(next));
  };
  const selectedOpts = options.filter((o) => set.has(o.value));
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid ? `${testid}-trigger` : undefined}
            className="flex w-full items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm text-left hover:bg-gray-50">
            <span className={cn('truncate', set.size === 0 && 'text-slate-400')}>
              {set.size === 0 ? placeholder : `${set.size} selected`}
            </span>
            <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
          <Command>
            <CommandInput placeholder="Search…" />
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem key={o.value} value={`${o.label} ${o.sublabel || ''}`}
                    data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                    onSelect={() => toggle(o.value)}>
                    <Check size={14} className={cn('mr-2', set.has(o.value) ? 'opacity-100 text-[#8FC93A]' : 'opacity-0')} />
                    <span className="flex flex-col">
                      <span>{o.label}</span>
                      {o.sublabel && <span className="text-xs text-slate-400">{o.sublabel}</span>}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedOpts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedOpts.map((o) => (
            <span key={o.value} className="inline-flex items-center gap-1 rounded-full bg-[#0E2A47]/5 px-2.5 py-1 text-xs text-[#0E2A47]">
              {o.label}
              <button type="button" onClick={() => toggle(o.value)} className="text-slate-400 hover:text-slate-700"
                data-testid={testid ? `${testid}-remove-${o.value}` : undefined}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
